import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { tankMixingService } from '../../../services/tankMixingService';
import { productService } from '../../../services/productService';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import { AlertTriangle, CheckCircle, Info, X, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const TankMixingCalculator = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [hasPremiumAccess, setHasPremiumAccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [walesProtocol, setWalesProtocol] = useState([]);
  const [compatibilityResult, setCompatibilityResult] = useState(null);
  const [procedureData, setProcedureData] = useState({
    procedureName: '',
    targetPest: '',
    tankSize: 100,
    waterPH: 7.0,
    waterTemp: 68,
    fieldLocation: '',
    notes: ''
  });
  const [showIncompatibleWarning, setShowIncompatibleWarning] = useState(false);

  useEffect(() => {
    loadTankMixingData();
  }, [user]);

  useEffect(() => {
    if (selectedProducts?.length >= 2) {
      checkCompatibility();
    } else {
      setCompatibilityResult(null);
      setShowIncompatibleWarning(false);
    }
  }, [selectedProducts]);

  const loadTankMixingData = async () => {
    if (!user) return;

    try {
      setLoading(true);
      
      const [premiumAccess, productsData, walesData] = await Promise.all([
        tankMixingService?.hasPremiumAccess(user?.id),
        productService?.getAllProducts(),
        tankMixingService?.getWalesMixingOrder()
      ]);

      setHasPremiumAccess(premiumAccess);
      setProducts(productsData);
      setWalesProtocol(walesData);
    } catch (error) {
      console.error('Error loading tank mixing data:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkCompatibility = async () => {
    try {
      const productIds = selectedProducts?.map(p => p?.id);
      const result = await tankMixingService?.checkProductCompatibility(productIds);
      setCompatibilityResult(result);
      
      // Show warning if dangerous combinations detected
      if (result?.dangerous_combinations?.length > 0) {
        setShowIncompatibleWarning(true);
      }
    } catch (error) {
      console.error('Error checking compatibility:', error);
    }
  };

  const addProduct = (product) => {
    if (!selectedProducts?.find(p => p?.id === product?.id)) {
      setSelectedProducts([...selectedProducts, { 
        ...product, 
        rate: '', 
        order: selectedProducts?.length + 1 
      }]);
    }
  };

  const removeProduct = (productId) => {
    setSelectedProducts(selectedProducts?.filter(p => p?.id !== productId));
  };

  const updateProductRate = (productId, rate) => {
    setSelectedProducts(selectedProducts?.map(p => 
      p?.id === productId ? { ...p, rate } : p
    ));
  };

  const saveProcedure = async () => {
    try {
      const procedureToSave = {
        ...procedureData,
        mixingProducts: selectedProducts,
        walesOrderFollowed: true,
        compatibilityWarnings: compatibilityResult?.warnings || [],
        totalCost: calculateTotalCost(),
        estimatedCoverage: calculateCoverage()
      };

      await tankMixingService?.saveTankMixingProcedure(user?.id, procedureToSave);
      
      // Reset form
      setSelectedProducts([]);
      setProcedureData({
        procedureName: '',
        targetPest: '',
        tankSize: 100,
        waterPH: 7.0,
        waterTemp: 68,
        fieldLocation: '',
        notes: ''
      });
      
      alert('Tank mixing procedure saved successfully!');
    } catch (error) {
      console.error('Error saving procedure:', error);
      alert('Failed to save procedure: ' + error?.message);
    }
  };

  const calculateTotalCost = () => {
    return selectedProducts?.reduce((total, product) => {
      const price = parseFloat(product?.price?.replace('$', '') || 0);
      return total + price;
    }, 0);
  };

  const calculateCoverage = () => {
    return procedureData?.tankSize * 0.025; // Rough estimate: 100 gallons covers ~2.5 acres
  };

  if (loading) {
    return (
      <div className="bg-card rounded-lg border border-border p-8 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-muted-foreground">Loading tank mixing calculator...</p>
      </div>
    );
  }

  if (!hasPremiumAccess) {
    return (
      <div className="bg-card rounded-lg border border-border p-8">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-yellow-400/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="Crown" size={32} className="text-yellow-600" />
          </div>
          <h3 className="text-xl font-semibold text-foreground mb-2">Premium Feature</h3>
          <p className="text-muted-foreground mb-4">
            Tank mixing procedures with compatibility warnings are available for Commercial Operation subscribers.
          </p>
          <Button
            onClick={() => navigate('/pricing-page')}
            className="bg-yellow-600 hover:bg-yellow-700 text-white"
          >
            Upgrade to Premium
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Incompatible Products Warning Modal */}
      {showIncompatibleWarning && compatibilityResult?.dangerous_combinations?.length > 0 && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-red-800">Dangerous Tank Mix Detected!</h3>
                  <p className="text-red-600 text-sm">This combination will stress plants and cause phytotoxicity</p>
                </div>
              </div>
              <button
                onClick={() => setShowIncompatibleWarning(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              {compatibilityResult?.dangerous_combinations?.map((combo, index) => (
                <div key={index} className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3 mb-3">
                    <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-red-800 mb-1">
                        NEVER MIX: {combo?.products?.join(' + ')}
                      </h4>
                      <p className="text-red-700 text-sm mb-2">{combo?.warning}</p>
                      <p className="text-red-600 text-xs mb-2">
                        <strong>Scientific Reason:</strong> {combo?.reason}
                      </p>
                      {combo?.alternative && (
                        <p className="text-red-600 text-xs">
                          <strong>Alternative:</strong> {combo?.alternative}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <Button
                variant="outline"
                onClick={() => setShowIncompatibleWarning(false)}
              >
                I Understand
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  // Remove incompatible products
                  const safeProducts = selectedProducts?.filter(product => {
                    return !compatibilityResult?.dangerous_combinations?.some(combo => 
                      combo?.products?.includes(product?.name)
                    );
                  });
                  setSelectedProducts(safeProducts);
                  setShowIncompatibleWarning(false);
                }}
              >
                Remove Incompatible Products
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Header */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
            <Icon name="Flask" className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-foreground">Tank Mixing Calculator</h2>
            <p className="text-sm text-muted-foreground">
              Professional tank mixing with compatibility warnings and W.A.L.E.S. protocol
            </p>
          </div>
        </div>

        {/* Procedure Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Input
            label="Procedure Name"
            placeholder="Spider Mite Treatment Mix"
            value={procedureData?.procedureName}
            onChange={(e) => setProcedureData({...procedureData, procedureName: e?.target?.value})}
          />
          <Input
            label="Target Pest/Disease"
            placeholder="Spider mites, preventive"
            value={procedureData?.targetPest}
            onChange={(e) => setProcedureData({...procedureData, targetPest: e?.target?.value})}
          />
          <Input
            label="Tank Size (Gallons)"
            type="number"
            placeholder="100"
            value={procedureData?.tankSize}
            onChange={(e) => setProcedureData({...procedureData, tankSize: parseFloat(e?.target?.value)})}
          />
          <Input
            label="Water pH"
            type="number"
            step="0.1"
            placeholder="7.0"
            value={procedureData?.waterPH}
            onChange={(e) => setProcedureData({...procedureData, waterPH: parseFloat(e?.target?.value)})}
          />
          <Input
            label="Water Temperature (°F)"
            type="number"
            placeholder="68"
            value={procedureData?.waterTemp}
            onChange={(e) => setProcedureData({...procedureData, waterTemp: parseFloat(e?.target?.value)})}
          />
          <Input
            label="Field Location"
            placeholder="North Field A1"
            value={procedureData?.fieldLocation}
            onChange={(e) => setProcedureData({...procedureData, fieldLocation: e?.target?.value})}
          />
        </div>
      </div>
      {/* Product Selection */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Select Products to Mix</h3>
        
        <div className="mb-6">
          <Select
            label="Add Product"
            options={products?.map(product => ({
              value: product?.id,
              label: `${product?.name} - ${product?.active_ingredients}`
            }))}
            onChange={(productId) => {
              const product = products?.find(p => p?.id === productId);
              if (product) addProduct(product);
            }}
            placeholder="Search and select products..."
            searchable
          />
        </div>

        {/* Selected Products */}
        {selectedProducts?.length > 0 && (
          <div className="space-y-3 mb-6">
            <h4 className="font-medium text-foreground">Selected Products ({selectedProducts?.length}):</h4>
            {selectedProducts?.map((product, index) => (
              <div key={product?.id} className="bg-muted/50 rounded-lg p-4 flex items-center justify-between">
                <div className="flex-1">
                  <h5 className="font-medium text-foreground">{product?.name}</h5>
                  <p className="text-sm text-muted-foreground">{product?.active_ingredients}</p>
                  {product?.special_notes && (
                    <p className="text-xs text-warning mt-1">Note: {product?.special_notes}</p>
                  )}
                </div>
                <div className="flex items-center space-x-3">
                  <Input
                    placeholder="Rate (e.g., 2 fl oz/gal)"
                    value={product?.rate}
                    onChange={(e) => updateProductRate(product?.id, e?.target?.value)}
                    className="w-32"
                  />
                  <span className="text-sm text-muted-foreground">Order: {index + 1}</span>
                  <button
                    onClick={() => removeProduct(product?.id)}
                    className="p-1 text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Compatibility Status */}
        {compatibilityResult && (
          <div className="space-y-4 mb-6">
            {compatibilityResult?.is_safe ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <h4 className="font-medium text-green-800">Tank Mix Compatible</h4>
                </div>
                <p className="text-green-700 text-sm mt-2">
                  This product combination is safe for tank mixing when following proper procedures.
                </p>
              </div>
            ) : (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center space-x-3">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                  <h4 className="font-medium text-red-800">Dangerous Tank Mix - DO NOT PROCEED</h4>
                </div>
                <p className="text-red-700 text-sm mt-2">
                  This combination contains incompatible products that will cause plant damage.
                </p>
              </div>
            )}

            {/* Warnings */}
            {compatibilityResult?.warnings?.length > 0 && (
              <div className="space-y-2">
                {compatibilityResult?.warnings?.map((warning, index) => (
                  <div key={index} className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <div className="flex items-start space-x-2">
                      <Info className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-yellow-800 text-sm font-medium">
                          Caution: {warning?.products?.join(' + ')}
                        </p>
                        <p className="text-yellow-700 text-xs">{warning?.warning}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      {/* W.A.L.E.S. Protocol Guide */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">
          W.A.L.E.S. Mixing Protocol
        </h3>
        <p className="text-sm text-muted-foreground mb-6">
          Follow this professional mixing order to prevent compatibility issues and equipment clogs.
        </p>
        
        <div className="space-y-3">
          {walesProtocol?.map((step, index) => (
            <div key={step?.step_number} className="bg-muted/30 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-sm">{step?.step_code}</span>
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-foreground">{step?.step_name}</h4>
                  <p className="text-sm text-muted-foreground mt-1">{step?.step_description}</p>
                  {step?.mixing_notes && (
                    <p className="text-xs text-blue-600 mt-2">
                      💡 Tip: {step?.mixing_notes}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Additional Notes */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-medium text-blue-800 mb-2">Important Safety Notes:</h4>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• Always read product labels before mixing</li>
            <li>• Never mix copper-based products with soap-based insecticides</li>
            <li>• Test small batches before full tank mixing</li>
            <li>• Maintain continuous agitation throughout mixing process</li>
            <li>• Apply mixed solutions within 24 hours for best results</li>
          </ul>
        </div>
      </div>
      {/* Save Procedure */}
      {selectedProducts?.length > 0 && (
        <div className="bg-card rounded-lg border border-border p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">Save Tank Mixing Procedure</h3>
          
          <div className="space-y-4 mb-6">
            <textarea
              className="w-full p-3 border border-input rounded-lg resize-none focus:ring-2 focus:ring-primary"
              rows="3"
              placeholder="Additional notes or observations..."
              value={procedureData?.notes}
              onChange={(e) => setProcedureData({...procedureData, notes: e?.target?.value})}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              <p>Estimated Cost: ${calculateTotalCost()?.toFixed(2)}</p>
              <p>Coverage: ~{calculateCoverage()?.toFixed(1)} acres</p>
            </div>
            
            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={() => {
                  setSelectedProducts([]);
                  setProcedureData(prev => ({...prev, notes: ''}));
                }}
              >
                Clear All
              </Button>
              <Button
                onClick={saveProcedure}
                disabled={!compatibilityResult?.is_safe || selectedProducts?.length < 2}
                className={compatibilityResult?.is_safe ? '' : 'opacity-50 cursor-not-allowed'}
              >
                Save Procedure
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TankMixingCalculator;